/**
 * Transaction Schema
 *
 * Canonical field definitions and CSV row <-> transaction object converters
 * for external-data transaction rows. Kept in parity with the backend's
 * canonical parser (NewServerless core-data/client/equity-transactions-scoped.py)
 * so the same source CSV produces the same canonical shape on both sides.
 *
 * Header-based parsing is primary: HEADER_ALIASES mirrors
 * STANDARD_TRANSACTION_HEADER_ALIASES in equity-transactions-scoped.py
 * field-for-field. Fixed-column parsing remains only as a fallback for
 * headerless CSVs, matching the backend's LEGACY_CSV_COLUMNS behavior.
 *
 * Cert #, Cert Sort, Reference Price, Exact Price, Vendor Code, and
 * Qty x (Ref) Price are NEVER round-tripped as raw stored columns:
 *   - certNumber is derived from `notes`/`description` at read time only, via
 *     getCertNumber() (string-utils.js) -- the single, universal cert-number
 *     entry point used everywhere in this codebase, including ReportEngine.js,
 *     custom-reports.js, and report-viewer.js. Not re-exported from here (see
 *     the note near the bottom of this file for why). fromCSVRow() does not
 *     read a "Cert #" column even when one is present in the source file;
 *     toCSVRow() never writes one.
 *   - referencePrice is a lookup against a client's separate `valuations`
 *     table (out of scope for a single-row converter -- see findReferencePrice
 *     in ledgers.js / getPayments in index.js for the lookup pattern).
 *     fromCSVRow()/toCSVRow() never read or write it.
 *   - Share Price / Exact Price consolidate to one canonical sharePrice via
 *     bestPrice(), mirroring _best_price() in equity-transactions-scoped.py.
 * This mirrors normalize_rows()'s READ_ONLY_TRANSACTION_HEADERS handling and
 * externalTxnStore's CANONICAL_HEADERS/READ_ONLY_HEADERS in ledgers.js.
 *
 * Consolidation status (#5621 follow-up, investigated 2026-07-29):
 *   - TransactionColumnRegistry.js / transaction-table-renderer.js need NOT
 *     be wired to this module -- both operate only on already-canonical,
 *     already-parsed transaction objects (accessors reading txn.sharePrice,
 *     txn.certNumber, etc.) and contain no CSV-parsing, header-alias, price-
 *     merge, or cert-derivation logic of their own to consolidate.
 *   - ledgers.js's externalTxnStore _does_ duplicate real logic here (header-
 *     alias resolution, read-only-header stripping, numeric cleaning) but is
 *     NOT a safe drop-in swap for fromCSVRow()/toCSVRow(): externalTxnStore
 *     writes into an existing file's actual (arbitrary) header layout and
 *     preserves unrecognized/custom columns, while toCSVRow() always emits a
 *     brand-new fixed 14-column row. Its READ_ONLY_HEADERS set is also
 *     broader than READ_ONLY_HEADER_LABELS below (it additionally strips
 *     Exact Price/Exact Cost/Reference Cost variants). Wiring it in as-is
 *     would either drop a client's custom columns or stop purging legacy
 *     price columns on save -- a regression, not a fix. A real consolidation
 *     would require this module to grow a header-aware write mode (map into
 *     an arbitrary existing layout, preserve unknown columns) plus a merged
 *     read-only-header list, before ledgers.js could safely switch over.
 *     Deliberately deferred; not attempted in this pass.
 */

import { dateStringToUnix } from './date-utils.js';

/**
 * Canonical transaction fields.
 * 'source' indicates whether the field is stored or computed at render/read
 * time. Fields marked 'computed' are never read from or written to a raw CSV
 * column by fromCSVRow()/toCSVRow() -- see file header.
 */
export const TRANSACTION_FIELDS = {
	shareClass: { type: 'string', required: true, source: 'stored' },
	date: { type: 'timestamp', required: true, source: 'stored' },
	securityType: { type: 'string', required: false, source: 'stored' },
	notes: { type: 'string', required: false, source: 'stored' },
	nrid: { type: 'string', required: true, source: 'stored' },
	senderName: { type: 'string', required: true, source: 'stored' },
	sender: { type: 'string', required: true, source: 'stored' },
	receiverName: { type: 'string', required: true, source: 'stored' },
	receiver: { type: 'string', required: true, source: 'stored' },
	quantity: { type: 'number', required: true, source: 'stored' },
	sharePrice: { type: 'number', required: true, source: 'stored' },
	strikePrice: { type: 'number', required: false, source: 'stored' },
	expDate: { type: 'timestamp', required: false, source: 'stored' },
	taxBasis: { type: 'number', required: false, source: 'stored' },
	// Never round-tripped through fromCSVRow/toCSVRow -- always derived
	// (certNumber) or looked up (referencePrice) by the consumer. See file header.
	certNumber: { type: 'string', required: false, source: 'computed' },
	referencePrice: { type: 'number', required: false, source: 'computed' },
	holder: { type: 'string', required: false, source: 'stored' },
	holderName: { type: 'string', required: false, source: 'stored' },
};

// Header aliases mirror STANDARD_TRANSACTION_HEADER_ALIASES in
// equity-transactions-scoped.py exactly, field-for-field. Intentionally has
// no entry for certNumber/referencePrice/vendorCode -- those headers are
// read-only import artifacts (see READ_ONLY_HEADER_LABELS) and are never
// mapped into the parsed transaction object.
export const HEADER_ALIASES = {
	shareClass: ['class', 'share class'],
	date: ['date', 'transaction date', 'trans date'],
	securityType: ['security type', 'type'],
	notes: ['notes', 'description', 'desc'],
	nrid: ['nrid'],
	senderName: ['sender name'],
	sender: ['sender', 'sender email'],
	receiverName: ['receiver name', 'recipient name'],
	receiver: ['receiver', 'receiver email', 'recipient', 'recipient email'],
	quantity: ['quantity', 'qty', 'shares', 'units'],
	sharePrice: ['price', 'share price'],
	exactPrice: ['exact price'],
	strikePrice: ['strike price', 'strike', 'hurdle', 'threshold'],
	expDate: ['expiration date', 'exp date', 'expiration'],
	taxBasis: ['tax basis', 'cost basis', 'basis'],
};

const REQUIRED_HEADER_FIELDS = ['shareClass', 'date', 'sender', 'receiver', 'quantity'];

// Read-only/import-only header names -- mirrors
// READ_ONLY_TRANSACTION_HEADER_LABELS in equity-transactions-scoped.py.
// Headers matching these are never mapped into the parsed row, whether or
// not they're also absent from HEADER_ALIASES above.
const READ_ONLY_HEADER_LABELS = [
	'cert number',
	'cert #',
	'certificate',
	'certificate number',
	'cert sort',
	'reference price',
	'ref price',
	'vendor code',
	'qty x price',
	'qty x ref. price',
];

function normalizeHeader(value) {
	let v = String(value ?? '').trim();
	v = v.replace(/([a-z])([A-Z])/g, '$1 $2'); // transactionDate -> transaction Date
	return v
		.toLowerCase()
		.replace(/[_\-.]/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
}

const READ_ONLY_HEADERS = new Set(READ_ONLY_HEADER_LABELS.map(normalizeHeader));

/**
 * Build a { field: columnIndex } map from a CSV header row, using the same
 * alias matching as equity-transactions-scoped.py's
 * _build_standard_transaction_header_map. Returns null when the required
 * fields (shareClass/date/sender/receiver/quantity) aren't all recognized --
 * callers should fall back to fixed-column parsing in that case.
 */
export function buildHeaderMap(headerRow) {
	const normalized = (headerRow || []).map(normalizeHeader);
	const headerMap = {};

	for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
		const normalizedAliases = new Set(aliases.map(normalizeHeader));
		const idx = normalized.findIndex((h) => normalizedAliases.has(h));
		if (idx >= 0) headerMap[field] = idx;
	}

	const isStandard = REQUIRED_HEADER_FIELDS.every((f) => f in headerMap);
	return isStandard ? headerMap : null;
}

function cleanNum(val) {
	if (val == null || val === '') return '';
	return String(val).replace(/[^.\d]/g, '');
}

function cleanNumSigned(val) {
	if (val == null || val === '') return '';
	return String(val).replace(/[^-.\d]/g, '');
}

function decimalPrecision(value) {
	const s = String(value ?? '').trim();
	if (!s) return -1;
	const cleaned = s.replace(/[^0-9.\-]/g, '');
	if (!cleaned || ['-', '.', '-.'].includes(cleaned)) return -1;
	if (!cleaned.includes('.')) return 0;
	return cleaned.split('.')[1].replace(/0+$/, '').length;
}

/**
 * Prefer whichever of Share Price / Exact Price has the greater decimal
 * precision -- mirrors _best_price() in equity-transactions-scoped.py exactly,
 * so the same source row produces the same canonical sharePrice on both sides.
 */
export function bestPrice(...values) {
	const candidates = values.filter((v) => String(v ?? '').trim());
	if (!candidates.length) return '';
	return candidates.reduce((best, v) => (decimalPrecision(v) > decimalPrecision(best) ? v : best));
}

// Legacy fixed-column layout, used only when a CSV has no recognizable header
// row -- mirrors LEGACY_CSV_COLUMNS in equity-transactions-scoped.py exactly.
// Note there is no Reference Price / Vendor Code / Cert # position: those are
// never a raw stored column, matching the header-based path above.
const FIXED_COLUMNS = [
	'shareClass',
	'date',
	'securityType',
	'notes',
	'nrid',
	'senderName',
	'sender',
	'receiverName',
	'receiver',
	'quantity',
	'sharePrice',
	null,
	null,
	null,
	null,
	'strikePrice',
	'expDate',
	'taxBasis',
];

/**
 * Parse a CSV row into a canonical transaction object.
 *
 * @param {string[]} row - CSV row as array of strings
 * @param {Object} [options]
 * @param {Object|null} [options.headerMap] - Result of buildHeaderMap(headerRow).
 *   Omit (or pass null) to use fixed-column fallback parsing.
 * @param {boolean} [options.isFund=false] - sharePrice forced to '1' (ACR/fund clients)
 * @returns {Object|null} Transaction object, or null if the row should be skipped
 *   (quantity is zero/empty)
 */
export function fromCSVRow(row, options = {}) {
	const { headerMap = null, isFund = false } = options;
	let txn;

	if (headerMap) {
		const get = (field) => (headerMap[field] != null ? String(row[headerMap[field]] ?? '').trim() : '');
		txn = {
			shareClass: get('shareClass'),
			date: dateStringToUnix(get('date')),
			securityType: get('securityType'),
			notes: get('notes'),
			nrid: get('nrid'),
			senderName: get('senderName'),
			sender: get('sender').toLowerCase(),
			receiverName: get('receiverName'),
			receiver: get('receiver').toLowerCase(),
			quantity: cleanNumSigned(get('quantity')),
			sharePrice: bestPrice(get('exactPrice'), get('sharePrice')),
			strikePrice: cleanNum(get('strikePrice')),
			expDate: dateStringToUnix(get('expDate')),
			taxBasis: cleanNumSigned(get('taxBasis')),
		};
	} else {
		const cell = (i) => (i < row.length ? String(row[i] ?? '').trim() : '');
		txn = {};
		FIXED_COLUMNS.forEach((field, i) => {
			if (field) txn[field] = cell(i);
		});
		txn.sender = (txn.sender || '').toLowerCase();
		txn.receiver = (txn.receiver || '').toLowerCase();
		txn.date = dateStringToUnix(txn.date);
		txn.quantity = cleanNumSigned(txn.quantity);
		txn.strikePrice = cleanNum(txn.strikePrice);
		txn.expDate = dateStringToUnix(txn.expDate);
		txn.taxBasis = cleanNumSigned(txn.taxBasis);
	}

	if (isFund) txn.sharePrice = '1';
	if (!Number(txn.quantity)) return null;
	if (!txn.strikePrice) delete txn.strikePrice;
	if (!txn.expDate) delete txn.expDate;
	if (!txn.taxBasis) delete txn.taxBasis;

	return txn;
}

/**
 * Convert a transaction object to a canonical CSV row: 14 fields, a single
 * price column, no Cert #/Reference Price/Vendor Code -- mirrors
 * externalTxnStore.CANONICAL_HEADERS/_recordToRow in ledgers.js exactly.
 * Never writes certNumber, referencePrice, or a second price column; those
 * are display/derived-only, computed by the reader, not persisted here.
 *
 * @param {Object} txn - Transaction object
 * @param {Object} [options]
 * @param {Function} [options.formatDate] - Format a unix timestamp to a date string
 * @param {Function} [options.csvEscape] - Escape CSV values (notes/names)
 * @param {boolean} [options.isFund=false] - Truncate the row after quantity for fund clients
 * @returns {string[]} CSV row as array of strings
 */
export function toCSVRow(txn, options = {}) {
	const fmt = options.formatDate || ((ts) => ts || '');
	const esc = options.csvEscape || ((v) => v);

	const row = [
		txn.shareClass || '',
		fmt(txn.date),
		txn.securityType || '',
		esc(txn.notes || ''),
		txn.nrid || '',
		esc(txn.senderName || ''),
		txn.sender || '',
		esc(txn.receiverName || ''),
		txn.receiver || '',
		cleanNumSigned(txn.quantity),
		cleanNum(txn.sharePrice),
		txn.strikePrice ? cleanNum(txn.strikePrice) : '',
		txn.expDate ? fmt(txn.expDate) : '',
		txn.taxBasis ? cleanNumSigned(txn.taxBasis) : '',
	];

	if (options.isFund) {
		row.splice(9);
		row.push(Number(cleanNumSigned(txn.quantity) || 0).toString());
	}

	return row;
}

// Cert numbers: as of #5621 (2026-07-29), this module deliberately does NOT
// export a cert-number helper. There is exactly one public way to get a cert
// number anywhere in this codebase now -- getCertNumber() in string-utils.js
// -- since deriving one correctly requires client-flags.js (isSheetz/isJMS),
// which this file intentionally does not depend on (it mirrors the backend's
// parser and stays free of DOM/client-flag concerns). Import getCertNumber
// directly from './string-utils.js' rather than looking for it here.
